// RTL mirroring for the reference's physical inline styles (left/right, margins, paddings, borders,
// radii, translateX, inset shadows). LTR output is the reference string unchanged. Cached per input.

const SWAP_PROPS: Record<string, string> = {};
const pairs: Array<[string, string]> = [
  ['left', 'right'], ['margin-left', 'margin-right'], ['padding-left', 'padding-right'],
  ['border-left', 'border-right'], ['border-left-color', 'border-right-color'], ['border-left-width', 'border-right-width'],
  ['border-left-style', 'border-right-style'], ['border-top-left-radius', 'border-top-right-radius'],
  ['border-bottom-left-radius', 'border-bottom-right-radius'],
];
for (const [a, b] of pairs) { SWAP_PROPS[a] = b; SWAP_PROPS[b] = a; }

const RTL_ANIM: Record<string, string> = { tpToast: 'tpToastRtl', tpPaneIn: 'tpPaneInRtl', tpUndoBar: 'tpUndoBar' };

function splitTop(s: string, sep: string): string[] {
  const out: string[] = []; let depth = 0, start = 0, q = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) { if (c === q) q = ''; continue; }
    if (c === '"' || c === '\'') q = c;
    else if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === sep && depth === 0) { out.push(s.slice(start, i)); start = i + 1; }
  }
  out.push(s.slice(start));
  return out;
}

const neg = (v: string) => /^-/.test(v) ? v.slice(1) : (/^0(px|%)?$/.test(v) ? v : '-' + v);
const swapWords = (v: string) => v.replace(/url\([^)]*\)/g, m => m.replace(/\b(left|right)\b/g, '\u0000$1')).replace(/(?<!\u0000)\b(left|right)\b/g, w => (w === 'left' ? 'right' : 'left')).replace(/\u0000/g, '');

function mirrorValue(prop: string, value: string): string {
  const v = value.trim();
  if (prop === 'text-align' || prop === 'float' || prop === 'clear') return v === 'left' ? 'right' : v === 'right' ? 'left' : v;
  if ((prop === 'margin' || prop === 'padding' || prop === 'border-width' || prop === 'border-color') && !v.includes('(')) {
    const p = v.split(/\s+/); return p.length === 4 ? [p[0], p[3], p[2], p[1]].join(' ') : v;
  }
  if (prop === 'border-radius') {
    const parts = v.split('/').map(x => x.trim());
    return parts.map(part => { const p = splitTop(part, ' ').filter(Boolean); if (p.length === 4) return [p[1], p[0], p[3], p[2]].join(' '); if (p.length === 2) return v.includes('/') ? part : part; return part; }).join(' / ');
  }
  if (prop === 'transform') return v.replace(/translateX\(([^)]+)\)/g, (_, x) => `translateX(${neg(x.trim())})`).replace(/translate\(([^,)]+),/g, (_, x) => `translate(${neg(x.trim())},`);
  if (prop === 'box-shadow') return splitTop(v, ',').map(sh => { const t = sh.trim().split(/\s+/); const i = t[0] === 'inset' ? 1 : 0; if (t[i] && /^-?[\d.]+(px)?$/.test(t[i]!)) t[i] = neg(t[i]!); return t.join(' '); }).join(', ');
  if (prop === 'background' || prop === 'background-position' || prop === 'transform-origin') return swapWords(v);
  if (prop === 'animation' || prop === 'animation-name') return v.replace(/\b(tp[A-Za-z]+)\b/g, m => RTL_ANIM[m] || m);
  return v;
}

export function bidiCss(css: string): string {
  return splitTop(css, ';').map(decl => {
    const i = decl.indexOf(':'); if (i < 0) return decl;
    const prop = decl.slice(0, i).trim().toLowerCase(), value = decl.slice(i + 1);
    return (SWAP_PROPS[prop] || prop) + ':' + mirrorValue(prop, value);
  }).join(';');
}

const cache = new Map<string, string>();
export function rtlStyle(css: string): string {
  let r = cache.get(css);
  if (r === undefined) { r = bidiCss(css); if (cache.size > 5000) cache.clear(); cache.set(css, r); }
  return r;
}
